import { useCallback, useRef } from 'react';
import * as React from 'react';

interface UseFieldArrayKeyboardParams {
  /** Callback to submit the form (triggered on Cmd/Ctrl+Enter) */
  onSubmit: () => void;
  /**
   * Optional callback to append a new row (triggered on plain Enter from an input).
   * When omitted, plain Enter simply prevents default form submission.
   */
  onAppendRow?: () => void;
  /**
   * Total number of rows currently in the field array.
   * Required only when `onAppendRow` is provided (used to calculate the focus target).
   */
  rowCount?: number;
  /**
   * The base field path used in `react-hook-form` (e.g. 'entities', 'projects').
   * Required only when `onAppendRow` is provided.
   */
  arrayFieldName?: string;
  /**
   * The `name` of the first field in each row — the one that gets focused after a new row is added.
   * Required only when `onAppendRow` is provided.
   */
  firstFieldName?: string;
}

/**
 * Generic hook for keyboard-driven form interaction.
 *
 * - **Cmd+Enter** (Mac) / **Ctrl+Enter** (Win) → submits the form.
 * - **Enter** on an input → appends a new row and auto-focuses its first field (when `onAppendRow` is provided).
 *   If `onAppendRow` is not provided, plain Enter is simply suppressed to prevent accidental form submission.
 *
 * Returns a `formRef` (attach to `<form>`) and a `handleKeyDown` handler (attach to `<form onKeyDown>`).
 *
 * @example
 * ```tsx
 * // With append-row behavior (field array forms)
 * const { formRef, handleKeyDown } = useFieldArrayKeyboard({
 *   onSubmit: () => handleSubmit(onSubmitFn)(),
 *   onAppendRow: () => append(defaultValues),
 *   rowCount: formFields.length,
 *   arrayFieldName: 'entities',
 *   firstFieldName: 'fullName',
 * });
 *
 * // Without append-row behavior (plain forms)
 * const { formRef, handleKeyDown } = useFieldArrayKeyboard({
 *   onSubmit: () => handleSubmit(onSubmitFn)(),
 * });
 *
 * <form ref={formRef} onKeyDown={handleKeyDown}>…</form>
 * ```
 */
export function useFieldArrayKeyboard({
  onSubmit,
  onAppendRow,
  rowCount,
  arrayFieldName,
  firstFieldName,
}: UseFieldArrayKeyboardParams) {
  const formRef = useRef<HTMLFormElement>(null);

  const focusNewRow = useCallback(
    (newRowIndex: number) => {
      if (!arrayFieldName || !firstFieldName) return;

      requestAnimationFrame(() => {
        if (!formRef.current) return;

        const newInput = formRef.current.querySelector<HTMLInputElement>(
          `[name="${arrayFieldName}.${newRowIndex}.${firstFieldName}"]`
        );
        newInput?.focus();
      });
    },
    [arrayFieldName, firstFieldName]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.key !== 'Enter') return;

      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        onSubmit();
        return;
      }

      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT') {
        e.preventDefault();

        if (onAppendRow) {
          const currentCount = rowCount ?? 0;
          onAppendRow();
          focusNewRow(currentCount);
        }
      }
    },
    [onSubmit, onAppendRow, rowCount, focusNewRow]
  );

  return { formRef, handleKeyDown };
}
